// RegionSelector — dropdown en el header para que el lector elija
// manualmente la región de la edición que quiere ver.
//
// UX:
// • Al abrir, carga los departamentos si aún no están en el contexto.
// • Muestra "Nacional" (edición global) como primera opción y luego
//   la lista alfabética de departamentos.
// • Cuando el usuario elige, escribe la preferencia en localStorage
//   (via context.setManualRegion) y refresca la última edición. La
//   selección manual gana sobre la detección automática.
// • Indica cuando la región activa fue detectada automáticamente
//   (geolocation / ip) para transparencia.

import { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, Check } from 'lucide-react';
import { useNewspaper } from '../../context/AppContext';
import styles from './RegionSelector.module.css';

export default function RegionSelector() {
  const { regionId, regionInfo, departamentos, loadDepartamentos, setManualRegion } = useNewspaper();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Cerrar al hacer clic afuera.
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleToggle = async () => {
    if (!open && departamentos.length === 0) {
      setLoading(true);
      await loadDepartamentos();
      setLoading(false);
    }
    setOpen((v) => !v);
  };

  const handlePick = (dept) => {
    setManualRegion({ departmentId: dept?.id ?? null, name: dept?.nombre ?? 'Nacional' });
    setOpen(false);
  };

  const activeName = regionInfo.isManual
    ? regionInfo.name || 'Nacional'
    : (departamentos.find(d => d.id === regionId)?.nombre || 'Nacional');

  const sourceHint = regionInfo.isManual
    ? 'Elección manual'
    : regionInfo.source === 'geolocation' ? 'Detectada por ubicación'
      : regionInfo.source === 'ip'          ? 'Detectada por IP'
      : 'Edición global';

  return (
    <div className={styles.wrapper} ref={ref}>
      <button className={styles.trigger} onClick={handleToggle} type="button">
        <MapPin size={14} />
        <span className={styles.label}>{activeName}</span>
        <ChevronDown size={12} />
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.hint}>{sourceHint}</div>

          <button
            type="button"
            className={styles.item}
            onClick={() => handlePick(null)}
            role="menuitem"
          >
            {regionId == null && <Check size={14} />}
            <span>Nacional (edición global)</span>
          </button>

          {loading && <div className={styles.loading}>Cargando…</div>}

          {!loading && departamentos.map((d) => (
            <button
              key={d.id}
              type="button"
              className={styles.item}
              onClick={() => handlePick(d)}
              role="menuitem"
            >
              {regionId === d.id && <Check size={14} />}
              <span>{d.nombre}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
